import axios from 'axios';
import { capitalize } from 'lodash';
import moment from 'moment';
import { decode } from 'base64-arraybuffer';
import { ILabourClocking } from '../models/dms/clocking.model';
import { IRepairOrderJob } from '../models/dms/repairOrderJob.model';
import { IThunkError } from '../models/error.model';
import { LogType } from '../models/log.model';
import storeRef from '../store';
import { logTransaction } from './log';
import { toastStatic } from '../providers/ToastProvider';
import { AttachmentAction, AttachmentAssetType, IAttachment, IBlobWithAttributes, MediaType } from '../models/dms/attachment.model';
import { CopyOptions, Directory, Filesystem } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';

let store: typeof storeRef | undefined;
export const injectStore = (_store: typeof storeRef) => {
  store = _store;
};

export const padLeft = (input: number) => {
  return input < 10 ? '0' + input : input.toString();
};

export const getDateTimeString = (inputDate: Date) => {
  return moment(inputDate).format('YYYY-MM-DDTHH:mm:ss');
};

export const getStringDateTime = (inputDate: string) => {
  return moment(inputDate, 'YYYY-MM-DDTHH:mm:ss');
};

export const getApiDateString = (dateInput: any, includeTimezone: boolean = false) => {
  let dateMoment = moment();

  if (moment.isMoment(dateInput)) {
    dateMoment = dateInput.clone();
  } else if (dateInput instanceof Date) {
    dateMoment = moment(dateInput);
  } else if (typeof dateInput === 'string') {
    dateMoment = moment(new Date(dateInput));
  }

  const dateOutput = dateMoment.format(`YYYY-MM-DDTHH:mm:ss${includeTimezone ? 'Z' : ''}`);

  return dateOutput === 'Invalid date' ? '' : dateOutput;
};

export const getApiDateMoment = (dateInput: string) => {
  return moment(dateInput, 'YYYY-MM-DDTHH:mm:ssZ');
};

export const getDisplayDate = (date: Date) => {
  return moment(date).format('MMM DD, YYYY HH:mm');
};

export const getShortDate = (date: Date) => {
  return moment(date).format('DD/MM/YYYY');
};

export const getHoursMinutes = (date?: Date) => {
  if (date) {
    return padLeft(date.getHours()) + ':' + padLeft(date.getMinutes());
  }
  return '--:--';
};

export const getDateTimeStringWithTimezone = (inputDate: Date) => {
  inputDate = inputDate || new Date();
  const timezone = getTimezoneOffset(inputDate);
  return `${getDateTimeString(inputDate)}${timezone}`;
};

export const getTimezoneOffset = (inputDate: Date): string => {
  inputDate = inputDate || new Date();
  const timezoneOffsetMin = inputDate.getTimezoneOffset();
  const offsetHrs = Math.abs(Math.trunc(timezoneOffsetMin / 60));
  const offsetMin = Math.abs(timezoneOffsetMin % 60);

  let offsetHrsStr = offsetHrs.toString();
  let offsetMinStr = offsetMin.toString();
  let timezoneStandard = '';

  if (offsetHrs < 10) {
    offsetHrsStr = '0' + offsetHrs;
  }

  if (offsetMin < 10) {
    offsetMinStr = '0' + offsetMin;
  }

  // Add an opposite sign to the offset
  // If offset is 0, it means timezone is UTC
  if (timezoneOffsetMin < 0) {
    timezoneStandard = '+' + offsetHrsStr + ':' + offsetMinStr;
  } else if (timezoneOffsetMin > 0) {
    timezoneStandard = '-' + offsetHrsStr + ':' + offsetMinStr;
  } else if (timezoneOffsetMin === 0) {
    timezoneStandard = 'Z';
  }

  return timezoneStandard;
};

export const stateDateFormat = 'YYYY-MM-DDTHH:mm:ss.SS';
export const getStateDateAsString = (date: Date) => {
  return getStateMomentAsString(moment(date));
};
export const getStateMomentAsString = (date: moment.Moment) => {
  return date.format(stateDateFormat);
};
export const getStateStringAsMoment = (date: string | undefined) => {
  date = date || moment().format(stateDateFormat);
  return moment(date, stateDateFormat);
};
export const getStateStringAsDate = (date: string | undefined) => {
  return getStateStringAsMoment(date).toDate();
};

export const handleApiError = (name: string, err: any) => {
  if (!axios.isCancel(err)) {
    logTransaction(LogType.Error, `Error on ${name}. ${err.message}`);
  }
  throw err;
};

export const handleThunkError = (err: any, rejection: Function, loading1?: { payload: number; type: string }, loading2?: { payload: boolean; type: string }): IThunkError | undefined => {
  loading1 && store?.dispatch(loading1);
  loading2 && store?.dispatch(loading2);
  if (!axios.isCancel(err)) {
    logTransaction(LogType.Error, `Thunk error ${err?.message || err}`);
    return rejection({ message: err?.message || err } as IThunkError);
  }
};

export const copyTextToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
};

export const flattenObject = (obj: any, parent?: string, res: any = {}) => {
  for (let key in obj) {
    let propName = parent ? parent + '.' + key : key;
    if (typeof obj[key] === 'object') {
      flattenObject(obj[key], propName, res);
    } else {
      res[propName] = obj[key];
    }
  }
  return res;
};

export const capitaliseWords = (words: string) => {
  const onlyWords = words.split(' ').filter((word) => word);
  return onlyWords.map((word) => capitalize(word)).join(' ');
};

export const getQueryString = (search: string) => {
  return new URLSearchParams(window.location.search).get(search) || '';
};

export const stringToDecimalString = (val?: number) => {
  return (val || 0).toFixed(2);
};

export const dateConvertString = (date: moment.Moment) => {
  return date.format('yyyy-MM-ddTHH:mm:ss.ms');
};

export const stringConvertDate = (date: string) => {
  return moment(date, 'yyyy-MM-ddTHH:mm:ss.ms');
};

export const stringifyJson = (json: object) => {
  try {
    const jsonString = JSON.stringify(json);
    return jsonString || '';
  } catch (err) {
    // Not json
    return json.toString() || '';
  }
};

export const empty = <T>(val?: T) => {
  if (val) {
    return Promise.resolve<T>(val);
  }
  return Promise.resolve<T>(undefined as any);
};

export const getClockingId = (labourClocking: ILabourClocking | undefined) => {
  if (labourClocking) {
    return `${labourClocking?.jobData?.jobType}${labourClocking?.jobData?.jobCode}${labourClocking?.jobData?.repairOrderNumber}`.toLowerCase();
  }
  return '';
};

export const getJobId = (job: IRepairOrderJob | undefined) => {
  if (job) {
    return `${job.jobType}${job.jobCode}${job.repairOrderNumber}`.toLowerCase();
  }
  return '';
};
export const createUUID = () => {
  const result = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

  return result;
};

export const generateGUID = () => {
  if (typeof window.crypto !== 'undefined' && typeof window.crypto.getRandomValues !== 'undefined') {
    const buf = new Uint32Array(4);
    window.crypto.getRandomValues(buf);
    let idx = -1;
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      idx++;
      const r = (buf[idx >> 3] >> ((idx % 8) * 4)) & 15;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  } else {
    let d = new Date().getTime();
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      d += performance.now();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (d + Math.random() * 16) % 16 | 0;
      d = Math.floor(d / 16);
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }
};

export const getEnumKeyByEnumValue = (myEnum: any, enumValue: number | string) => {
  let keys = Object.keys(myEnum).filter((x) => myEnum[x] === enumValue);
  return keys.length > 0 ? keys[0] : '';
};

export const encodeInvalidCharacters = (uriComponent: string) => {
  uriComponent = uriComponent || '';

  // "/" character cannot be handled by endpoint. It is treated like a new URI componet
  // even if the "/" character is URL encoded. Replace with custom marker.
  const slashCharacterTranslation = ']047[';
  uriComponent = (uriComponent || '').toString().replace(/\//gi, slashCharacterTranslation);

  // "\" character cannot be handled by endpoint. It is treated like a new URI componet
  // even if the "\" character is URL encoded. Replace with custom marker.
  const backSlashCharacterTranslation = ']092[';
  uriComponent = (uriComponent || '').toString().replace(/\\/gi, backSlashCharacterTranslation);

  // "*" character is considered a dangerous input. The web service will throw a security error
  // if this character is used as a URL parameter. Replace with custom marker.
  const starCharacterTranslation = ']042[';
  uriComponent = (uriComponent || '').toString().replace(/\*/gi, starCharacterTranslation);

  // "&" character is considered a dangerous input. The web service will throw a security error
  // if this character is used as a URL parameter. Replace with custom marker.
  const ampersandCharacterTranslation = ']038[';
  uriComponent = (uriComponent || '').toString().replace(/&/gi, ampersandCharacterTranslation);

  // "." character before the URL slash cannot be handled by the endpoint. Replace with custom marker.
  const dotCharacterTranslation = ']046[';
  uriComponent = (uriComponent || '').toString().replace(/\./gi, dotCharacterTranslation);

  // ":" character before the URL slash cannot be handled by the endpoint. Replace with custom marker.
  const colonCharacterTranslation = ']058[';
  uriComponent = (uriComponent || '').toString().replace(/:/gi, colonCharacterTranslation);

  // "?" character before the URL slash cannot be handled by the endpoint. Replace with custom marker.
  const questionMarkCharacterTranslation = ']063[';
  uriComponent = (uriComponent || '').toString().replace(/\?/gi, questionMarkCharacterTranslation);

  // """ character before the URL slash cannot be handled by the endpoint. Replace with custom marker.
  const quotationMarkCharacterTranslation = ']034[';
  uriComponent = (uriComponent || '').toString().replace(/"/gi, quotationMarkCharacterTranslation);

  // ">" character before the URL slash cannot be handled by the endpoint. Replace with custom marker.
  const greaterThanCharacterTranslation = ']062[';
  uriComponent = (uriComponent || '').toString().replace(/>/gi, greaterThanCharacterTranslation);

  // "<" character before the URL slash cannot be handled by the endpoint. Replace with custom marker.
  const lessThanCharacterTranslation = ']060[';
  uriComponent = (uriComponent || '').toString().replace(/</gi, lessThanCharacterTranslation);

  // "|" character before the URL slash cannot be handled by the endpoint. Replace with custom marker.
  const BarCharacterTranslation = ']124[';
  uriComponent = (uriComponent || '').toString().replace(/\|/gi, BarCharacterTranslation);

  // Encode using browsers built-in encoding
  return uriComponent;
};

export const decodeInvalidCharacters = (encodedString: string) => {
  encodedString = encodedString || '';

  // Reverse the encoding process
  // "/"
  encodedString = encodedString.replace(/\]047\[/gi, '/');
  // "\"
  encodedString = encodedString.replace(/\]092\[/gi, '\\');
  // "*"
  encodedString = encodedString.replace(/\]042\[/gi, '*');
  // "&"
  encodedString = encodedString.replace(/\]038\[/gi, '&');
  // "."
  encodedString = encodedString.replace(/\]046\[/gi, '.');
  // ":"
  encodedString = encodedString.replace(/\]058\[/gi, ':');
  // "?"
  encodedString = encodedString.replace(/\]063\[/gi, '?');
  // """
  encodedString = encodedString.replace(/\]034\[/gi, '"');
  // ">"
  encodedString = encodedString.replace(/\]062\[/gi, '>');
  // "<"
  encodedString = encodedString.replace(/\]060\[/gi, '<');
  // "|"
  encodedString = encodedString.replace(/\]124\[/gi, '|');

  return encodedString;
};

export const roundOff = (num: number, places: number) => {
  const x = Math.pow(10, places);
  return Math.round(num * x) / x || 0;
};

export const handleClickPhoneNumber = async (phoneNumber: string | number | boolean) => {
  try {
    // if (phoneNumber) {
    //   const res = await CallNumber.callNumber(phoneNumber.toString(), true);
    // }
  } catch (err) {
    toastStatic.errorToast(`Error launching dialer. ${err}`);
  }
};

export const delay = async (delay: number) => {
  const promise = new Promise<boolean>((res) => {
    setTimeout(() => {
      res(true);
    }, delay);
  });
  return promise;
};

export const infiniteTimer = (delay: number, callback: (index: number) => void) => {
  let timeout: NodeJS.Timeout | undefined;
  let index = 0;

  const start = () => {
    timeout = setTimeout(() => {
      callback(index++);
      start();
    }, delay);
  };

  const stop = () => {
    clearTimeout(timeout);
  };

  return { start, stop };
};

export const getSrcFromAttachment = async (attachment: IAttachment) => {
  try {
    logTransaction(LogType.Info, `Loading image from pathAndName: ${attachment.webPath}`);
    if (attachment.path) {
      const blob = await systemPathToBlob(attachment.path, attachment.mimeType || 'image/png');
      if (blob) {
        const url = URL.createObjectURL(blob.blob);
        logTransaction(LogType.Info, `Image loaded from pathAndName`);
        return url;
      } else {
        throw new Error('No blob found in attachment data');
      }
    } else {
      logTransaction(LogType.Info, `No pathAndName found in attachment data`);
    }

    logTransaction(LogType.Info, `Loading image from base64: ${attachment.base64Url?.substring(0, 40)}`);
    if (attachment.base64Url) {
      const blob = base64UrlToBlob(attachment.base64Url, attachment.fileName, attachment.mimeType || 'image/png');
      if (blob) {
        const url = URL.createObjectURL(blob.blob);
        logTransaction(LogType.Info, `Image loaded from pathAndName`);
        return url;
      } else {
        throw new Error('No blob found in attachment data');
      }
    } else {
      logTransaction(LogType.Info, `No base64Url found in attachment data`);
    }

    logTransaction(LogType.Info, `Loading image from webPath: ${attachment.webPath}`);
    if (attachment.webPath) {
      const blob = await webPathToBlob(attachment.webPath);
      if (blob) {
        const url = URL.createObjectURL(blob.blob);
        logTransaction(LogType.Info, `Image loaded from webPath`);
        return url;
      } else {
        throw new Error('No blob found in attachment data');
      }
    } else {
      throw new Error('No webPath found in attachment data');
    }
  } catch (err: any) {
    logTransaction(LogType.Error, `Failed to load attachment: ${err.message}`);
  }
};

export const webPathToBase64Url = async (uri: string): Promise<string | undefined> => {
  logTransaction(LogType.Info, `Converting webPath to base64Url: ${uri}`);
  const blob = await webPathToBlob(uri);
  if (blob) {
    return await blobToBase64Url(blob.blob);
  }
  return undefined;
};

export const webPathToBlob = async (uri: string, path?: string, webPath?: string): Promise<IBlobWithAttributes | undefined> => {
  logTransaction(LogType.Info, `Convert photo URI to Blob: ${uri}`);
  if (!uri) return undefined;

  logTransaction(LogType.Info, `Fetching photo file from URI`);
  const response = await fetch(uri);
  logTransaction(LogType.Info, `Got photo file response: ${response.status}, creating blob`);
  const blob = await response.blob();

  // Map the MIME type to an appropriate file extension
  const extension = blob.type.split('/')[1] || 'png';
  let fileName = `${uri.split('/').pop() || 'photo'}`;
  if (!fileName.endsWith(extension)) {
    fileName = `${fileName}.${extension}`;
  }
  logTransaction(LogType.Info, `Photo file created: ${fileName} | ${blob.type}`);

  return { blob, fileName, mimeType: blob.type, path, webPath };
};

export const systemPathToBase64Url = async (filePath: string, mimeType: string): Promise<string | undefined> => {
  logTransaction(LogType.Info, `Converting system path to base64Url: ${filePath}`);
  const blob = await systemPathToBlob(filePath, mimeType);
  if (blob) {
    return await blobToBase64Url(blob.blob);
  }
  return undefined;
};

export const systemPathToBlob = async (filePath: string, mimeType: string, path?: string, webPath?: string): Promise<IBlobWithAttributes | undefined> => {
  try {
    logTransaction(LogType.Info, `Reading file from path: ${filePath}`);
    const contents = await Filesystem.readFile({ path: filePath });
    // Extract the base file name without path
    const fileName = filePath.split('/').pop() || `unknown-${generateGUID()}`;
    logTransaction(LogType.Info, `Filename is: ${fileName}`);

    return base64ToBlob(contents.data as string, fileName, mimeType, path, webPath);
  } catch (ex: any) {
    logTransaction(LogType.Error, `Failed to fetch file from system: ${ex.message}`);
  }
};

export const blobToBase64Url = (blob: Blob): Promise<string> => {
  logTransaction(LogType.Info, `Converting blob to base64Url for file: ${blob.name} | ${blob.type}`);
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (e) => {
      const errorMessage = reader.error?.message || 'Unknown error';
      logTransaction(LogType.Error, `Failed to convert file to base64: ${blob.name}. Error: ${errorMessage}`);
      reject(reader.error || e);
    };
    reader.readAsDataURL(blob);
  });
};

export const base64UrlToBlob = (base64Url: string, filename: string, mimeType: string): IBlobWithAttributes => {
  logTransaction(LogType.Info, `Converting base64Url [${base64Url.substring(0, 40)}...] to blob for file: ${filename} | ${mimeType}`);
  const base64Data = base64Url.split(',')[1];
  return base64ToBlob(base64Data, filename, mimeType);
};

export const base64ToBlob = (base64: string, fileName: string, mimeType: string, path?: string, webPath?: string): IBlobWithAttributes => {
  try {
    logTransaction(LogType.Info, `Converting base64 [${base64.substring(0, 40)}...] to blob for type: ${mimeType}`);
    const byteArray = new Uint8Array(decode(base64));
    const blob = new Blob([byteArray], { type: mimeType });
    logTransaction(LogType.Info, `Blob created for file: ${fileName} | ${mimeType}`);
    logTransaction(LogType.Info, `Blob type: ${blob.type} | Blob size: ${blob.size}`);
    return { blob, fileName, mimeType, path, webPath };
  } catch (ex: any) {
    logTransaction(LogType.Error, `Failed to convert base64 to blob: ${ex.message}`);
    throw ex;
  }
};

export const addMedia = async (blob: IBlobWithAttributes, mediaType: MediaType, assetType: AttachmentAssetType, description: string) => {
  try {
    logTransaction(LogType.Info, `Add media attachment requested`);
    logTransaction(LogType.Info, `Media type is ${mediaType}`);

    if (blob && (mediaType === MediaType.Image || mediaType === MediaType.Video)) {
      logTransaction(LogType.Info, `Creating media attachment object`);
      let mediaAsAttachment = {
        action: AttachmentAction.Add,
        lineNumber: new Date().getTime(),
        fileName: blob.fileName,
        path: blob.path,
        webPath: blob.webPath,
        base64Url: Capacitor.getPlatform() === 'web' ? await blobToBase64Url(blob.blob) : undefined,
        mimeType: blob.mimeType,

        description,
        assetType,
        mediaType,
      } as IAttachment;

      if (Capacitor.isNativePlatform()) {
        // On native, copy file from cache location to data directory
        const copyConfig = {
          from: mediaAsAttachment.path,
          to: mediaAsAttachment.fileName,
          toDirectory: Directory.Data,
        } as CopyOptions;
        logTransaction(LogType.Info, `Copying media file to data directory: ${JSON.stringify(copyConfig)}`);
        const copyRes = await Filesystem.copy(copyConfig);
        mediaAsAttachment.path = copyRes.uri;
      }

      return mediaAsAttachment;
    }
  } catch (err: any) {
    throw new Error(`Error adding media attachment: ${err.message}`);
  }
};

export const mediaFileNameRegex = /[\\/]?a-([a-z0-9]+)-((?:\[\[[^=]+=[\s\S]*?\]\])+)-(image|video)-([a-f0-9]{32})\.([a-z0-9]+)$/i;
export const mediaFileNameLabelRegex = /\[\[([^=]+)=([\s\S]*?)\]\]/gi;
export interface ParsedFilename<TLabels extends Record<string, string> = Record<string, string>> {
  key: string;
  labels: TLabels;
  mediaType: 'image' | 'video';
  guid: string;
  extension: string;
}

export const parseFilename = <TLabels extends Record<string, string> = Record<string, string>>(filename: string): ParsedFilename<TLabels> | null => {
  const result = mediaFileNameRegex.exec(filename);
  if (!result) return null;

  const [, key, labelSection, mediaType, guid, extension] = result;

  const labels = {} as TLabels;
  let match: RegExpExecArray | null;

  while ((match = mediaFileNameLabelRegex.exec(labelSection)) !== null) {
    const itemKey = match[1] as keyof TLabels;
    const value = match[2];
    labels[itemKey] = value as TLabels[keyof TLabels];
  }

  return {
    key,
    labels,
    mediaType: mediaType as 'image' | 'video',
    guid,
    extension,
  };
};
